import React, { useState, useEffect, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useStripe, useElements } from '@stripe/react-stripe-js';
import { ErrorMessage } from '../common';
import LoadingSpinner from '../ui/LoadingSpinner';
import { formatCurrency } from '../../utils/formatters';
import { 
  setPaymentMetadata,
  setStrapiPaymentId,
  setPaymentIntent,
} from '../../redux/slices/checkoutSlice';
import { 
  usePrepareCheckoutMutation,
} from '../../redux/apis/endpoints/paymentApi';
import { isItemUnshippable } from './utils/checkoutUtils';
import { calculateOrderAmounts } from './utils/paymentUtils';
import StripeCardForm from './components/StripeCardForm';
import { selectAllCartItems } from '../../redux/slices/cartSlice';

const PaymentSection = ({ 
  disabled,
  onComplete,
  onError,
  setLoading,
}) => {
  const [error, setError] = useState(null);
  const [cardComplete, setCardComplete] = useState(false);
  const [saveCard, setSaveCard] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [cardElementReady, setCardElementReady] = useState(false);
  const [showCardElement, setShowCardElement] = useState(false);
  const [cardKey, setCardKey] = useState(1); 
  
  const stripe = useStripe();
  const elements = useElements();
  const dispatch = useDispatch();
  
  const [prepareCheckoutMutation, { isLoading: isPreparingCheckout }] = usePrepareCheckoutMutation();

  const {
    shippingAddress,
    selectedShippingRate,
    billingAddress,
    unshippableItems = {},
    checkoutCurrency,
    deliveryGroups,
  } = useSelector(state => state.checkout);
  const { user } = useSelector(state => state.auth);
  
  const allCartItemsFromStore = useSelector(selectAllCartItems);
  
  const currencyFilteredItems = useMemo(() => {
    const currencyUpperCase = (checkoutCurrency || 'USD').toUpperCase();
    return allCartItemsFromStore
      .filter(item => {
        const itemCurrency = (item.currency || 'USD').toUpperCase();
        return itemCurrency === currencyUpperCase;
      })
      .map(item => {
        if (item.type === 'rent') {
          return {
            ...item,
            rentOption: item.rentalPeriod || item.rentTimeSelect || (item.rentPriceAndTime && item.rentPriceAndTime.length > 0 ? item.rentPriceAndTime[0] : null)
          };
        }
        return item;
      });
  }, [allCartItemsFromStore, checkoutCurrency]);

  const shippableItemsForPayload = useMemo(() => {
    return currencyFilteredItems
      .filter(item => !isItemUnshippable(item, unshippableItems))
      .map(item => {
        const itemDeliveryMethod = item.deliveryMethod;

        return {
          productId: item.id,
          quantity: item.quantity,
          variantId: item.variantId || item.productOptions?.map(opt => opt.value).join('-') || null,
          merchantId: item.merchant?.id,
          deliveryMethod: itemDeliveryMethod,
          type: item.type,
          rentOption: item.type === 'rent' ? item.rentOption : null,
          selectedShippoRateId: itemDeliveryMethod === 'shipping' ? item.selectedShippoRateId : null,
          pickupAddressId: itemDeliveryMethod === 'pickup' ? item.pickupAddressId : null,
        };
      });
  }, [currencyFilteredItems, unshippableItems]);
  
  const displayAmount = useMemo(() => {
    if (!shippableItemsForPayload.length) return 0;

    // Calculate shipping total for display purposes
    let displayShippingTotal = 0;
    const uniqueDisplayRates = new Map();
    
    // Add main selected shipping rate if it exists
    if (selectedShippingRate && selectedShippingRate.object_id) {
      uniqueDisplayRates.set(selectedShippingRate.object_id, parseFloat(selectedShippingRate.amount) || 0);
    }
    
    // Add all rates from shipping address groups
    if (deliveryGroups?.shipping?.addressGroups) {
      Object.values(deliveryGroups.shipping.addressGroups).forEach(group => {
        if (group.rate && group.rate.object_id && group.rate.amount) {
          uniqueDisplayRates.set(group.rate.object_id, parseFloat(group.rate.amount) || 0);
        } else if (group.rate && group.rate.id && group.rate.price) {
          // Some rates might use price instead of amount
          uniqueDisplayRates.set(group.rate.id, parseFloat(group.rate.price) || 0);
        }
      });
    }
    
    // Sum up all unique rates
    displayShippingTotal = Array.from(uniqueDisplayRates.values()).reduce((sum, amount) => sum + amount, 0);

    const { totalAmount: calculatedDisplayTotal } = calculateOrderAmounts(
      currencyFilteredItems.filter(item => !isItemUnshippable(item, unshippableItems)),
      displayShippingTotal
    );
    return calculatedDisplayTotal;
  }, [shippableItemsForPayload, currencyFilteredItems, unshippableItems, selectedShippingRate, deliveryGroups]);

  const unshippableItemCount = useMemo(() => {
    return currencyFilteredItems.filter(item => isItemUnshippable(item, unshippableItems)).length;
  }, [currencyFilteredItems, unshippableItems]);

  const hasValidItemsForPayload = useMemo(() => shippableItemsForPayload.length > 0, [shippableItemsForPayload]);

  useEffect(() => {
  }, [hasValidItemsForPayload, cardComplete, stripe, elements, cardElementReady]);
  
  useEffect(() => {
    const timer = setTimeout(() => setShowCardElement(true), 500);
    return () => clearTimeout(timer);
  }, []);
  
  useEffect(() => {
    if (stripe && elements) console.log('Stripe and Elements are ready');
    if (elements) setError(null);
  }, [stripe, elements]);
  
  const handleSubmit = async (event) => {
    console.log('[PaymentSection/handleSubmit] Execution started.');
    event.preventDefault();

    const currentCheckoutCurrency = checkoutCurrency.toUpperCase();

    if (!stripe || !elements || !cardElementReady) {
      setError("Stripe has not been properly initialized. Please wait a moment or refresh.");
      onError("Stripe has not been properly initialized.");
      return;
    }
    
    const cardElement = elements.getElement('card');
    if (!cardElement || !cardComplete) {
      setError("Please enter complete card information.");
      onError("Please enter complete card information.");
      return;
    }
    
    setLoading(true);
    setIsProcessing(true);
    setError(null);
    
    console.log('[PaymentSection] Cart items for payload construction:', JSON.stringify(currencyFilteredItems, null, 2));

    const currentShippableItemsForPayload = currencyFilteredItems.map(item => {
      const itemDeliveryMethod = item.deliveryMethod;
      let finalSelectedShippoRateId = null;

      if (itemDeliveryMethod === 'shipping' && item.selectedShippoRateId) {
        // Find the group this item belongs to. 
        // The item.shippingAddress is the ID of the merchant's address record used as origin.
        const itemShippingOriginAddressId = item.shippingAddress; 
        const groupForThisItem = deliveryGroups?.shipping?.addressGroups?.[itemShippingOriginAddressId?.toString()];

        if (groupForThisItem && groupForThisItem.rate && groupForThisItem.rate.id === item.selectedShippoRateId) {
          if (groupForThisItem.rate.currency === currentCheckoutCurrency) {
            finalSelectedShippoRateId = item.selectedShippoRateId;
          } else {
            console.warn(`[PaymentSection/itemLoop] Item ${item.id} (rate ID: ${item.selectedShippoRateId}) is associated with group rate currency ${groupForThisItem.rate.currency}, which differs from checkout currency ${currentCheckoutCurrency}. Nullifying its selectedShippoRateId for payload.`);
            // finalSelectedShippoRateId remains null
          }
        } else {
           console.warn(`[PaymentSection/itemLoop] Could not find matching group rate in deliveryGroups for item ${item.id} (shippingAddress ID: ${itemShippingOriginAddressId}, selectedRateId: ${item.selectedShippoRateId}). Or, item's selected rate ID doesn't match group's selected rate. Nullifying its selectedShippoRateId for payload.`);
           // finalSelectedShippoRateId remains null
        }
      }

      return {
        productId: item.id,
        quantity: item.quantity,
        variantId: item.variantId || item.product?.variantId || null,
        merchantId: item.merchant?.id || item.users_permissions_user,
        deliveryMethod: itemDeliveryMethod,
        type: item.type || (item.rentPriceAndTime?.length > 0 ? 'rent' : 'buy'),
        rentOption: item.rentOption || null,
        selectedShippoRateId: finalSelectedShippoRateId, // Use the currency-validated rate ID
        pickupAddressId: itemDeliveryMethod === 'pickup' ? item.pickupAddressId : null,
      };
    });

    console.log('[PaymentSection] currentShippableItemsForPayload (after currency validation):', JSON.stringify(currentShippableItemsForPayload, null, 2));

    const ratesToUseInPayload = [];
    const uniqueRateIds = new Set();

    console.log(`[PaymentSection/handleSubmit] Target checkout currency for rates: ${currentCheckoutCurrency}`);
    console.log('[PaymentSection/handleSubmit] Reading deliveryGroups.shipping.addressGroups from Redux:', JSON.stringify(deliveryGroups?.shipping?.addressGroups, null, 2));

    if (deliveryGroups?.shipping?.addressGroups) {
      Object.entries(deliveryGroups.shipping.addressGroups).forEach(([addressId, group]) => {
        console.log(`[PaymentSection/handleSubmit] Processing group for addressId ${addressId}:`, JSON.stringify(group, null, 2));
        if (group.rate && group.rate.id) {
          if (group.rate.currency === currentCheckoutCurrency) {
            if (!uniqueRateIds.has(group.rate.id)) {
              console.log(`[PaymentSection/handleSubmit] Adding rate for group ${addressId} (ID: ${group.rate.id}, Currency: ${group.rate.currency}) to ratesToUseInPayload.`);
              const { id, price, ...restOfRate } = group.rate;
              ratesToUseInPayload.push({ 
                object_id: id,
                amount: price,
                ...restOfRate
              }); 
              uniqueRateIds.add(group.rate.id);
            } else {
              console.log(`[PaymentSection/handleSubmit] Rate ID ${group.rate.id} for group ${addressId} already added. Skipping.`);
            }
          } else {
            console.warn(`[PaymentSection/handleSubmit] Rate for group ${addressId} (ID: ${group.rate.id}) has currency ${group.rate.currency}, but checkout currency is ${currentCheckoutCurrency}. Skipping.`);
          }
        } else {
          console.warn(`[PaymentSection/handleSubmit] Group ${addressId} has no rate or rate ID. Skipping.`);
        }
      });
    }
    console.log('[PaymentSection/handleSubmit] Final ratesToUseInPayload (for selectedShippingRatesDetails):', JSON.stringify(ratesToUseInPayload, null, 2));

    // +++ Additional Debugging Logs +++
    console.log('[PaymentSection/handleSubmit] DEBUG: ratesToUseInPayload just before assignment:', ratesToUseInPayload);
    console.log('[PaymentSection/handleSubmit] DEBUG: ratesToUseInPayload.length just before assignment:', ratesToUseInPayload.length);
    // +++++++++++++++++++++++++++++++++++

    const prepareCheckoutPayload = {
      shippableItems: currentShippableItemsForPayload,
      shippingAddressId: shippingAddress.id,
      shippingChoice: ratesToUseInPayload,
      targetCurrency: currentCheckoutCurrency,
    };
    
    // +++ Temporarily display payload in UI +++
    // setDebugPayloadDisplay(JSON.stringify(prepareCheckoutPayload, null, 2));
    // +++++++++++++++++++++++++++++++++++++++++

    console.log('[PaymentSection] Calling prepareCheckout with payload:', JSON.stringify(prepareCheckoutPayload, null, 2));

    try {
      if (!shippingAddress || !shippingAddress.id) {
        throw new Error("Shipping address is not selected or invalid.");
      }
      if (!checkoutCurrency) {
        throw new Error("Target currency is not set.");
      }

      for (const item of currentShippableItemsForPayload) {
        if (!item.deliveryMethod) {
          throw new Error(`Item ${item.productId} is missing deliveryMethod.`);
        }
        if (item.deliveryMethod === 'shipping' && !item.selectedShippoRateId) {
          throw new Error(`Shipping Item ${item.productId} is missing selectedShippoRateId.`);
        }
        if (item.deliveryMethod === 'pickup' && !item.pickupAddressId) {
          throw new Error(`Pickup Item ${item.productId} is missing pickupAddressId.`);
        }
      }

      const prepareResult = await prepareCheckoutMutation(prepareCheckoutPayload).unwrap();
      console.log('[PaymentSection] prepareCheckout response:', prepareResult);

      if (!prepareResult.success || !prepareResult.clientSecret || !prepareResult.strapiPaymentId) {
        throw new Error(prepareResult.message || 'Failed to prepare checkout with the backend.');
      }

      const { clientSecret, strapiPaymentId: receivedStrapiPaymentId } = prepareResult;

      dispatch(setStrapiPaymentId(receivedStrapiPaymentId));
      dispatch(setPaymentIntent({ clientSecret, paymentIntentId: null }));
      
      const result = await stripe.confirmCardPayment(clientSecret, {
        payment_method: {
          card: cardElement,
          billing_details: {
            name: user?.username || 'Guest User',
            email: user?.email,
            address: {
              line1: billingAddress?.street || shippingAddress?.street,
              city: billingAddress?.city || shippingAddress?.city,
              state: billingAddress?.state || shippingAddress?.state,
              postal_code: billingAddress?.zip || shippingAddress?.zip,
              country: billingAddress?.country || shippingAddress?.country || 'US',
            },
          },
        },
      });

      if (result.error) {
        throw new Error(result.error.message);
      }

      if (result.paymentIntent && result.paymentIntent.status === 'succeeded') {
        console.log('[PaymentSection] Stripe payment successful:', result.paymentIntent);
        dispatch(setPaymentMetadata({
          paymentIntentId: result.paymentIntent.id,
          amount: result.paymentIntent.amount / 100,
        }));
        onComplete(result.paymentIntent.id, receivedStrapiPaymentId);
      } else {
        throw new Error('Payment did not succeed or has an unexpected status.');
      }
      
    } catch (err) {
      console.error('PaymentSection handleSubmit error:', err);
      const message = err.message || 'An error occurred during payment processing.';
      setError(message);
      onError(message);
    } finally {
      setLoading(false);
      setIsProcessing(false);
    }
  };
  
  const handleCardChange = (e) => {
    setCardComplete(e.complete);
    setError(e.error ? e.error.message : null);
  };

  const handleCardReady = () => {
    setCardElementReady(true);
    console.log('CardElement is ready and mounted');
  };
  
  const paymentButtonDisabled = disabled ||
                              isProcessing ||
                              isPreparingCheckout ||
                              !hasValidItemsForPayload ||
                              !stripe || !elements || !cardComplete || !cardElementReady;
  
  return (
    <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
      <h2 className="text-xl font-semibold mb-4">Payment</h2>
      
      {unshippableItemCount > 0 && (
        <div className="bg-yellow-50 border border-yellow-200 p-4 rounded-md mb-4">
          <p className="text-yellow-700 text-sm">
            {unshippableItemCount} item{unshippableItemCount > 1 ? 's' : ''} cannot be shipped to the selected address
            {hasValidItemsForPayload ? ' and have been excluded from the payment calculation.' : '.'}
          </p>
        </div>
      )}
      
      {!hasValidItemsForPayload ? (
        <div className="bg-red-50 border border-red-200 p-4 rounded-md mb-4">
          <p className="text-red-700">
            None of your items can be delivered. Please add deliverable items or change address.
          </p>
          <div className="mt-4 border-t border-red-200 pt-4">
            <p className="text-sm text-gray-700">
              <span className="font-medium">Total payment:</span> {formatCurrency(0, checkoutCurrency)}
            </p>
          </div>
        </div>
      ) : (
        <>
          <div className="mb-4">
            <p className="text-sm text-gray-700 mb-2">
              <span className="font-medium"> Total:</span> {formatCurrency(displayAmount, checkoutCurrency)}
            </p>
              <p className="text-xs text-gray-500">
              {unshippableItemCount > 0 && " Only includes items deliverable to your address."}
              </p>
          </div>
          
          <StripeCardForm 
            cardKey={cardKey}
            showCardElement={showCardElement}
            onCardChange={handleCardChange}
            onCardReady={handleCardReady}
            disabled={disabled || !hasValidItemsForPayload || isPreparingCheckout || isProcessing}
          />
          
          <div className="mb-6">
            <label className="flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={saveCard}
                onChange={() => setSaveCard(!saveCard)}
                disabled={disabled || !hasValidItemsForPayload || isPreparingCheckout || isProcessing}
                className="h-4 w-4 text-indigo-600"
              />
              <span className="ml-2 text-sm text-gray-700">Save this card for future purchases</span>
            </label>
          </div>
          
          <button
            type="button"
            onClick={handleSubmit}
            disabled={paymentButtonDisabled}
            className={`w-full py-3 px-4 rounded-lg font-medium ${
              !paymentButtonDisabled
                ? 'bg-blue-600 text-white hover:bg-blue-700'
                : 'bg-gray-300 text-gray-500 cursor-not-allowed'
            }`}
          >
            {(isProcessing || isPreparingCheckout) ? <LoadingSpinner /> :
             !hasValidItemsForPayload ? 'No Shippable Items' :
             `Confirm and Pay`}
          </button>
        </>
      )}
      
      {/* +++ Temporary UI Debug Display +++ */}
      {/* {debugPayloadDisplay && (  // Removed for cleanup
        <div className="mt-4 p-4 bg-gray-100 border border-gray-300 rounded">
          <h3 className="font-semibold text-red-600">DEBUG: Payload to Backend</h3>
          <pre className="text-xs whitespace-pre-wrap break-all">{debugPayloadDisplay}</pre>
        </div>
      )} */}
      {/* ++++++++++++++++++++++++++++++++ */}
      
      {error && <ErrorMessage message={error} className="mt-4" />}
    </div>
  );
};

export default PaymentSection; 